import { useCollectiblesFilter } from "./useCollectiblesFilter";
import { mapFilterSlugsToIds, useFilterSearch, type MappedFilterValues } from "@/composables/filter";
import { searchCollectiblesElastic, type CollectibleElastic } from "@/services/collectibles/elastic";
import { FilterType, IDENTIFICATION_FILTER_SLUG, type FilterDefinition, type SortDefinition } from "@/services/filter";
import { fetchCategoryByUrl } from "@/services/collection/categories";
import { useGettext } from "vue3-gettext";

const DEFAULT_PER_PAGE = 30;

type CollectiblesSearchRequest = {
  categoryId: number;
  page: number;
  perPage: number;
  filters: MappedFilterValues;
  identification?: string;
};

/**
 * Collectibles-specific search composable.
 * Uses useCollectiblesFilter() + useFilterSearch() with domain-specific API calls.
 */
export function useCollectiblesSearch(perPage: number = DEFAULT_PER_PAGE) {
  const { $gettext } = useGettext();
  const { categoryUrl, filterValues, page, setFilter, setRangeFilter, setPage, resetFilters } = useCollectiblesFilter();

  // Static identification filter definition (always first)
  const identificationFilter: FilterDefinition = {
    id: 0, // Special ID for identification filter
    slug: IDENTIFICATION_FILTER_SLUG,
    name: $gettext("Název"),
    type: FilterType.Text,
    values: [],
  };

  const apiFilterDefinitions = ref<FilterDefinition[]>([]);
  const sortDefinition = ref<SortDefinition | null>(null);

  // Combine identification filter with API filter definitions
  const filterDefinitions = computed<FilterDefinition[]>(() => [identificationFilter, ...apiFilterDefinitions.value]);

  // Computed trigger that combines all reactive dependencies
  const searchTrigger = computed(() => ({
    categoryUrl: categoryUrl.value,
    filterValues: filterValues.value,
    page: page.value,
  }));

  // Track if we have URL filters on first load (before definitions are available)
  const hadFiltersOnFirstLoad = Object.keys(filterValues.value).length > 0;

  const { items, totalItems, isLoading, error, refetch } = useFilterSearch<CollectibleElastic, CollectiblesSearchRequest>(searchTrigger, {
    perPage,
    buildRequest: async (filters, currentPage, itemsPerPage) => {
      const category = await fetchCategoryByUrl(categoryUrl.value);
      if (category === undefined) {
        throw new Error("Category not found");
      }

      // Extract identification value before mapping (it's not in API definitions)
      const identification = typeof filters[IDENTIFICATION_FILTER_SLUG] === "string" ? filters[IDENTIFICATION_FILTER_SLUG] : undefined;

      // Remove identification from filters before mapping to IDs
      const filtersWithoutIdentification = { ...filters };
      delete filtersWithoutIdentification[IDENTIFICATION_FILTER_SLUG];

      const filtersRemaped = mapFilterSlugsToIds(filtersWithoutIdentification, apiFilterDefinitions.value);

      return {
        categoryId: category.id,
        page: currentPage,
        perPage: itemsPerPage,
        filters: filtersRemaped,
        identification: identification || undefined,
      };
    },
    executeSearch: async (request, options) => {
      const response = await searchCollectiblesElastic(request, options);

      apiFilterDefinitions.value = response.parameters;
      // sortDefinition.value = response.sort; @todo Not implemented in API yet
      return {
        items: response.items,
        totalItems: response.totalItems,
      };
    },
  });

  // When filter definitions are first loaded and we had URL filters, refetch with proper mapping
  watch(apiFilterDefinitions, (definitions, oldDefinitions) => {
    if (definitions.length > 0 && (!oldDefinitions || oldDefinitions.length === 0) && hadFiltersOnFirstLoad) {
      refetch();
    }
  });

  // Compute active filter count based on known filter definitions
  const activeFilterCount = computed(() => {
    let count = 0;

    // Count identification filter if set
    const identificationValue = filterValues.value[IDENTIFICATION_FILTER_SLUG];
    if (identificationValue && identificationValue !== "") {
      count++;
    }

    // Count API filters
    if (apiFilterDefinitions.value.length > 0) {
      const filterFields = new Set(apiFilterDefinitions.value.map((f) => f.slug));

      for (const [key, value] of Object.entries(filterValues.value)) {
        // Skip sort field and identification (already counted)
        if (sortDefinition.value && key === sortDefinition.value.field) continue;
        if (key === IDENTIFICATION_FILTER_SLUG) continue;

        // Check if this is a known filter field (handle range _min/_max)
        const baseField = key.replace(/_min$|_max$/, "");
        if (filterFields.has(baseField) && value !== undefined) {
          if (Array.isArray(value) ? value.length > 0 : value !== "") {
            count++;
          }
        }
      }
    }

    return count;
  });

  return {
    categoryUrl,
    filterValues,
    page,
    setFilter,
    setRangeFilter,
    setPage,
    resetFilters,
    items,
    totalItems,
    filterDefinitions,
    sortDefinition,
    isLoading,
    error,
    activeFilterCount,
    refetch,
  };
}
